import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, patch, post } from "../../lib/request"
import {
  marketingSettingsToPropertyMetrics,
  propertyMetricsToMarketingSettings,
  unwrapMarketingSettingsResponse,
} from "../../components/organization-settings/propertyMetricsStorage"

const basePath = (clientId) => `client-marketing-settings/clients/${clientId}`

function isNotFoundError(error) {
  const msg = String(error?.message || "").toLowerCase()
  return msg.includes("404") || msg.includes("not found")
}

/** GET /api/v1/client-marketing-settings/clients/{clientId} */
export const fetchClientMarketingSettings = createAsyncThunk(
  "clientMarketingSettings/fetch",
  async (clientId, { rejectWithValue }) => {
    if (clientId == null || clientId === "") {
      return rejectWithValue("Missing client id")
    }
    try {
      const response = await get(basePath(clientId))
      const data = unwrapMarketingSettingsResponse(response)
      return {
        clientId: String(clientId),
        data,
        hasRecord: data != null,
      }
    } catch (error) {
      if (isNotFoundError(error)) {
        return { clientId: String(clientId), data: null, hasRecord: false }
      }
      return rejectWithValue(error?.message || String(error))
    }
  },
)

/** POST when no settings exist; PATCH when GET returned a record. */
export const saveClientMarketingSettings = createAsyncThunk(
  "clientMarketingSettings/save",
  async ({ clientId, metrics }, { getState, rejectWithValue }) => {
    if (clientId == null || clientId === "") {
      return rejectWithValue("Missing client id")
    }
    const body = propertyMetricsToMarketingSettings(metrics)
    const hasRecord = Boolean(getState().clientMarketingSettings?.hasRecord)

    try {
      const response = hasRecord
        ? await patch(basePath(clientId), body)
        : await post(basePath(clientId), body)
      const data = unwrapMarketingSettingsResponse(response) ?? { ...body }
      return {
        clientId: String(clientId),
        data,
        hasRecord: true,
        metrics: marketingSettingsToPropertyMetrics(data),
      }
    } catch (error) {
      return rejectWithValue(error?.message || String(error))
    }
  },
)

const initialState = {
  data: null,
  hasRecord: false,
  metrics: null,
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  lastFetchedClientId: null,
}

const clientMarketingSettingsSlice = createSlice({
  name: "clientMarketingSettings",
  initialState,
  reducers: {
    clearClientMarketingSettings: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientMarketingSettings.pending, (state, action) => {
        state.loading = true
        state.error = null
        if (String(state.lastFetchedClientId ?? "") !== String(action.meta.arg ?? "")) {
          state.data = null
          state.hasRecord = false
          state.metrics = null
        }
      })
      .addCase(fetchClientMarketingSettings.fulfilled, (state, action) => {
        state.loading = false
        state.lastFetchedClientId = action.payload.clientId
        state.data = action.payload.data
        state.hasRecord = action.payload.hasRecord
        state.metrics = marketingSettingsToPropertyMetrics(action.payload.data)
      })
      .addCase(fetchClientMarketingSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? "Failed to load property metrics"
      })
      .addCase(saveClientMarketingSettings.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(saveClientMarketingSettings.fulfilled, (state, action) => {
        state.saving = false
        state.data = action.payload.data
        state.hasRecord = true
        state.metrics = action.payload.metrics
        state.lastFetchedClientId = action.payload.clientId
      })
      .addCase(saveClientMarketingSettings.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.payload ?? "Failed to save property metrics"
      })
  },
})

export const { clearClientMarketingSettings } = clientMarketingSettingsSlice.actions
export default clientMarketingSettingsSlice.reducer
